import { createApp } from 'vue'
import { createPinia } from 'pinia'
import * as Sentry from '@sentry/vue'
import router from './router'
import App from './App.vue'
import './assets/css/main.css'
import { useAuthStore } from '@/modules/auth/stores/useAuthStore'
import { logger } from '@/core/utils/logger'
import { storeResetPlugin } from '@/core/plugins/storeResetPlugin'
import { i18n } from '@/locales'

const app = createApp(App)

const pinia = createPinia()
pinia.use(storeResetPlugin)
app.use(pinia)
app.use(i18n)

// Initialize theme
import { themePlugin } from '@/plugins/theme'
app.use(themePlugin)

app.use(router)

// Initialize Sentry in production
if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration()
    ],

    // Free tier - snížit sample rate
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Filtrovat zbytečné chyby
    beforeSend(event) {
      // Ignorovat extension/ad errors
      if (event.exception?.values?.[0]?.stacktrace?.frames?.some(
        frame => frame.filename?.includes('extension://') ||
                 frame.filename?.includes('chrome-extension://')
      )) {
        return null
      }

      // Sanitizovat sensitive data
      if (event.request?.cookies) {
        delete event.request.cookies
      }
      if (event.request?.headers?.['Authorization']) {
        event.request.headers['Authorization'] = '[Filtered]'
      }

      return event
    },

    environment: import.meta.env.MODE,
    release: __APP_VERSION__
  })
}

// Error handler - použít Sentry v produkci, console.error v developmentu
app.config.errorHandler = (err, instance, info) => {
  if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
    Sentry.captureException(err, {
      contexts: {
        vue: {
          componentName: instance?.$options?.name || 'Unknown',
          errorInfo: info
        }
      }
    })
  } else {
    logger.error('Vue error:', err)
    logger.error('Error info:', info)
    // Note: Vue instance is not logged to avoid exposing sensitive data
  }
}

const showApp = () => {
  const loader = document.getElementById('app-loader')
  if (loader) {
    loader.style.display = 'none'
  }

  const appElement = document.getElementById('app')
  if (appElement) {
    appElement.style.visibility = 'visible'
  }
}

const preloadData = async () => {
  const authStore = useAuthStore()

  // Always show app after router is ready, regardless of auth state
  const showAppWhenReady = () => {
    router.isReady().then(() => {
      const currentRoute = router.currentRoute.value

      if (!currentRoute.meta.public && !authStore.isAuthenticated) {
        router.push({ name: 'login' }).finally(() => {
          showApp()
        })
      } else {
        showApp()
      }
    })
  }

  // If not authenticated, just show the app
  if (!authStore.isAuthenticated) {
    showAppWhenReady()
    return
  }

  // Set a hard timeout for the entire initialization process
  const initTimeout = setTimeout(() => {
    logger.warn('App initialization timeout - clearing auth and showing login')
    // Clear stale auth data
    authStore.user = null
    showAppWhenReady()
  }, 8000) // 8 second hard timeout

  try {
    // First, refresh the access token using HTTP-only refresh token cookie
    // This must happen BEFORE any other API calls to prevent multiple 401 errors
    logger.log('Refreshing access token on app init...')
    await authStore.refreshTokens()
    logger.log('Access token refreshed successfully')

    // Now fetch user profile with valid access token
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Profile fetch timeout')), 5000)
    )

    await Promise.race([
      authStore.refreshUser(),
      timeoutPromise
    ])

    clearTimeout(initTimeout)
    showAppWhenReady()
  } catch (error: unknown) {
    clearTimeout(initTimeout)
    logger.error('Failed during app initialization:', error)

    // Clear auth state - user will be redirected to login
    // Don't call logout() as it may hang waiting for API
    authStore.user = null

    showAppWhenReady()
  }
}

app.mount('#app')

preloadData()
